import { AssetContainerEventType } from '@nova/core';

import type { YardAssetRow } from '@satellite/composables/useYardAssetRowsPresenter';

/** Listed and selectable (`true`), listed but not selectable (`'disabled'`), or not listed. */
export type YardAssetSelectAxis = boolean | 'disabled';

/** The one state a picker row is in. Resolved by {@link resolveAssetSelectState}. */
export type YardAssetSelectState = 'inTransit' | 'missing' | 'assigned' | 'waitingAssignment';

/** One axis per state, plus the orthogonal question of an open move task holding the asset. */
export interface YardAssetSelectAxes {
  includeAssignedAssets?: YardAssetSelectAxis;
  includeWaitingAssignmentAssets?: YardAssetSelectAxis;
  includeMissingAssets?: YardAssetSelectAxis;
  includeInTransitAssets?: YardAssetSelectAxis;
  includeAssociatedToActiveMoveTasks?: YardAssetSelectAxis;
}

const isAssetOnASpot = (asset: YardAssetRow): boolean => Boolean(asset.spot?.id);

/**
 * The single state a row is in, so the four state axes never overlap: an unassigned missing asset
 * is `missing`, not also `waitingAssignment`, and answering "list missing" no longer depends on
 * what the caller said about waiting assignment.
 *
 * Status wins over placement, and it is read off the row's own status — which the BFF already
 * reduced from the container's events — not off the spot the container sits on. A move task merely
 * targeting that spot says nothing about the asset, and an asset in transit belongs to no spot at
 * all. The order is the one the pickers already render: state first, spot second.
 */
export function resolveAssetSelectState(asset: YardAssetRow): YardAssetSelectState {
  if (asset.status === AssetContainerEventType.InTransit) {
    return 'inTransit';
  }

  if (asset.status === AssetContainerEventType.Missing) {
    return 'missing';
  }

  return isAssetOnASpot(asset) ? 'assigned' : 'waitingAssignment';
}

export const isAssetAssigned = (asset: YardAssetRow): boolean =>
  resolveAssetSelectState(asset) === 'assigned';

export const isAssetWaitingAssignment = (asset: YardAssetRow): boolean =>
  resolveAssetSelectState(asset) === 'waitingAssignment';

export const isAssetMissing = (asset: YardAssetRow): boolean =>
  resolveAssetSelectState(asset) === 'missing';

export const isAssetInTransit = (asset: YardAssetRow): boolean =>
  resolveAssetSelectState(asset) === 'inTransit';

/** Orthogonal to the state above: a parked asset can perfectly well hold an open move task. */
export const isAssetInActiveMoveTask = (asset: YardAssetRow): boolean =>
  (asset.openMoveTasks?.length ?? 0) > 0;

interface YardAssetSelectRule {
  axis: keyof YardAssetSelectAxes;
  matches: (asset: YardAssetRow) => boolean;
  /**
   * What the axis means when the caller says nothing. The three original axes exclude their state
   * unless asked for; the two added by OD-4127 stay inert, so a picker written before them keeps
   * listing missing and in-transit assets exactly as it does today.
   */
  fallback: YardAssetSelectAxis;
}

const YARD_ASSET_SELECT_RULES: readonly YardAssetSelectRule[] = [
  { axis: 'includeAssignedAssets', matches: isAssetAssigned, fallback: false },
  { axis: 'includeWaitingAssignmentAssets', matches: isAssetWaitingAssignment, fallback: false },
  {
    axis: 'includeAssociatedToActiveMoveTasks',
    matches: isAssetInActiveMoveTask,
    fallback: false
  },
  { axis: 'includeMissingAssets', matches: isAssetMissing, fallback: true },
  { axis: 'includeInTransitAssets', matches: isAssetInTransit, fallback: true }
];

const resolveAxis = (axes: YardAssetSelectAxes, rule: YardAssetSelectRule): YardAssetSelectAxis =>
  axes[rule.axis] ?? rule.fallback;

/** An asset matching any axis the caller left out is not listed at all. */
export const isAssetExcludedFromSelect = (
  asset: YardAssetRow,
  axes: YardAssetSelectAxes
): boolean => YARD_ASSET_SELECT_RULES.some(rule => !resolveAxis(axes, rule) && rule.matches(asset));

/** An asset matching any `'disabled'` axis is listed, greyed out and unselectable. */
export const isAssetDisabledInSelect = (asset: YardAssetRow, axes: YardAssetSelectAxes): boolean =>
  YARD_ASSET_SELECT_RULES.some(
    rule => resolveAxis(axes, rule) === 'disabled' && rule.matches(asset)
  );
